import { createContext, useContext } from 'react';
import { useSnackbar } from 'notistack';

const NotificationContext = createContext({});

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // Show success notification
  const showSuccess = (message, options = {}) => {
    return enqueueSnackbar(message, {
      variant: 'success',
      autoHideDuration: 4000,
      anchorOrigin: {
        vertical: 'top',
        horizontal: 'right',
      },
      ...options,
    });
  };

  // Show error notification
  const showError = (message, options = {}) => {
    return enqueueSnackbar(message, {
      variant: 'error',
      autoHideDuration: 6000,
      anchorOrigin: {
        vertical: 'top',
        horizontal: 'right',
      },
      ...options,
    });
  };

  // Show warning notification
  const showWarning = (message, options = {}) => {
    return enqueueSnackbar(message, {
      variant: 'warning',
      autoHideDuration: 5000,
      anchorOrigin: {
        vertical: 'top',
        horizontal: 'right',
      },
      ...options,
    });
  };

  // Show info notification
  const showInfo = (message, options = {}) => {
    return enqueueSnackbar(message, {
      variant: 'info',
      autoHideDuration: 4000,
      anchorOrigin: {
        vertical: 'top',
        horizontal: 'right',
      },
      ...options,
    });
  };

  // Close notification
  const closeNotification = (key) => {
    closeSnackbar(key);
  };

  // Close all notifications
  const closeAllNotifications = () => {
    closeSnackbar();
  };

  const value = {
    showSuccess,
    showError,
    showWarning,
    showInfo,
    closeNotification,
    closeAllNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
